import {
  ActionTooltip,
  ActionTooltipProps,
} from "common/Tooltips/ActionTooltip";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import React, { CSSProperties, useContext, useRef } from "react";
import { AvoContext } from "analytics/AvoProvider";
import IntercomLink from "common/Text/IntercomLink";
import { OnlyHelperTooltipProps } from "@shared/types";
import styled from "styled-components";
import { useBottomNavigator } from "utils/hooks/misc";
import BottomDrawer from "common/Drawers/BottomDrawer";
import Text from "common/Typography/Text";
import { BOTTOM_NAVIGATOR } from "common/constants.ts";

export type HelperTooltipProps = Omit<ActionTooltipProps, "title" | "body"> &
  OnlyHelperTooltipProps & {
    iconStyle?: CSSProperties;
    triggerElement?: JSX.Element;
    containerStyle?: CSSProperties;
    title?: string;
  };
const USER_HELP_ANALYTICS_DELAY = 2000;

export const HelperTooltip = ({
  description,
  articleLink,
  articleId,
  avoFieldName,
  avoPageName,
  iconStyle,
  triggerElement,
  containerStyle: propsContainerStyle,
  tooltipStyle,
  title,
  ...tooltipProps
}: HelperTooltipProps) => {
  const { avo } = useContext(AvoContext);
  const bottomNavigatorData = useBottomNavigator();

  const userHoverTimeData = useRef({
    tooltipOpenTime: 0,
  });

  const onTooltipVisibleChanged = (isVisible: boolean) => {
    const nowTimestamp = new Date().getTime();
    if (isVisible) {
      userHoverTimeData.current.tooltipOpenTime = nowTimestamp;
    } else {
      const timePassed =
        nowTimestamp - userHoverTimeData.current.tooltipOpenTime;
      if (timePassed >= USER_HELP_ANALYTICS_DELAY) {
        avo?.infoTooltipReviewed({
          infoTooltipNameAndLocation: `${avoPageName} - ${avoFieldName}`,
          infoTooltipReviewTime: timePassed,
        });
      }
    }
  };

  if (!avoPageName) return null;

  const getDescription = () => {
    if (Array.isArray(description)) {
      return description.map((item, index) => <div key={index}>{item}</div>);
    }
    return description;
  };

  const content = (
    <>
      <Description as="div">{getDescription()}</Description>
      {(articleLink || articleId) && (
        <IntercomLink
          articleId={articleId}
          articleLink={articleLink}
          style={{ display: "block", fontStyle: "italic" }}
        >
          Learn More
        </IntercomLink>
      )}
    </>
  );

  const trigger = triggerElement ?? <StyledInfoIcon style={iconStyle} />;

  const containerStyle = {
    marginLeft: 8,
    display: "inline-flex",
    alignItems: "center",
    ...propsContainerStyle,
  };

  if (bottomNavigatorData.shouldDisplay) {
    return (
      <BottomDrawer
        title={title ?? avoFieldName}
        content={content}
        triggerElement={<div style={containerStyle}>{trigger}</div>}
      />
    );
  }

  return (
    <ActionTooltip
      {...tooltipProps}
      onOpenChanged={onTooltipVisibleChanged}
      containerStyle={containerStyle}
      tooltipStyle={tooltipStyle}
      title={content}
    >
      <div>{trigger}</div>
    </ActionTooltip>
  );
};

const StyledInfoIcon = styled(InfoCircleOutlined)`
  overflow: visible;
  svg {
    overflow: visible;
  }
`;
const Description = styled(Text)`
  color: ${(p) => p.theme.colors.white};
  @media (max-width: ${BOTTOM_NAVIGATOR}) {
    color: ${(p) => p.theme.colors.primaryGrey};
  }
`;
